import { v4 as uuidv4 } from "uuid";

export const MediaRequestType = {
  Create: 1,
  Update: 2,
  Delete: 3,
} as const;
export type MediaRequestType =
  (typeof MediaRequestType)[keyof typeof MediaRequestType];

export const getFrontendFileType = (
  fileName: string,
  mimeType: string,
): string => {
  const lowerMime = mimeType?.toLowerCase() || "";
  const ext = fileName.split(".").pop()?.toLowerCase() || "";

  if (
    lowerMime.startsWith("image/") ||
    ["jpg", "jpeg", "png", "gif", "webp", "bmp"].includes(ext)
  )
    return "image";

  if (
    lowerMime.startsWith("video/") ||
    ["mp4", "webm", "avi", "mkv", "mov"].includes(ext)
  )
    return "video";

  if (
    lowerMime.startsWith("audio/") ||
    ["mp3", "wav", "aac", "ogg"].includes(ext)
  )
    return "audio";

  if (
    lowerMime.includes("pdf") ||
    lowerMime.includes("document") ||
    ["pdf", "doc", "docx", "xls", "xlsx", "txt"].includes(ext)
  )
    return "document";

  return "other";
};

export const buildUploadPayload = (entityId: string, files: File[]) => {
  return files.map((file) => {
    const id = uuidv4();
    const ext = file.name.split(".").pop()?.toLowerCase() || "file";

    return {
      id,
      file,
      payload: {
        id,
        requestType: MediaRequestType.Create,
        fileName: file.name,
        originalFileName: file.name,
        fileType: getFrontendFileType(file.name, file.type),
        mimeType: file.type || ext,
        fileSize: file.size,
        s3Url: id,
        entityId,
        tags: [],
        metadata: ext.toUpperCase(),
        isProfilePic: false,
        mediaType: 1,
      },
    };
  });
};

export const uploadMediaFiles = async (
  api: any,
  entityId: string,
  files: File[],
  groupId?: string,
) => {
  const mediaMap = buildUploadPayload(entityId, files);

  const ids = mediaMap.map((m) => m.id);

  const initRes = await api.initUpload(ids, groupId);

  await Promise.all(
    mediaMap.map(async (item) => {
      const uploadUrl = initRes?.links?.find((l: any) => l.id === item.id)?.t;
      // No link means no file: fail rather than record media that points at
      // nothing (it would show as a broken tile forever).
      if (!uploadUrl) throw new Error(`No upload link was issued for ${item.file.name}.`);
      await api.uploadToS3(uploadUrl, item.file);
    }),
  );

  await api.uploadMedia(
    entityId,
    mediaMap.map((m) => m.payload),
  );

  const linkRes = await api.getMediaLink(ids, 3, groupId);

  return {
    payloads: mediaMap,
    links: linkRes.links || [],
  };
};

export const createResolvedMedia = (
  payloads: any[],
  links: any[],
  entityId: string,
  userId: string,
) => {
  return links.map((link, index) => ({
    ...payloads[index].payload,
    requestType: MediaRequestType.Update,
    s3Url: link.id,
    url: link.link,
    entityId,
    modifiedBy: userId,
    modifiedDate: new Date().toISOString(),
  }));
};

export const buildRenamePayload = (
  media: any,
  entityId: string,
  newName: string,
) => {
  const ext = newName.split(".").pop()?.toLowerCase() || "file";

  return [
    {
      id: media.id,
      requestType: MediaRequestType.Update,
      fileName: newName,
      originalFileName: media.originalFileName || media.fileName,
      fileType: media.fileType || "document",
      mimeType: media.mimeType || ext,
      fileSize: media.fileSize,
      s3Url: media.s3Url,
      entityId,
      tags: media.tags || [],
      metadata: ext.toUpperCase(),
      isProfilePic: media.isProfilePic || false,
      mediaType: media.mediaType || 1,
    },
  ];
};

export const applyRename = (medias: any[], fileId: string, newName: string) => {
  return medias.map((m) =>
    m.id === fileId
      ? {
          ...m,
          fileName: newName,
          metadata: newName.split(".").pop()?.toUpperCase() || m.metadata,
        }
      : m,
  );
};

export const buildDeletePayload = (
  medias: any[],
  entityId: string,
  idsToRemove: string[],
) => {
  // Only the files being removed. Sending the whole list (every other file
  // as an Update) meant one stale or unknown sibling id failed the server's
  // existence check and blocked the delete outright.
  return medias.filter((media) => idsToRemove.includes(media.id)).map((media) => {
    const ext = (media.fileName || media.originalFileName || "").split(".").pop()?.toLowerCase() || "";

    return {
      id: media.id,
      requestType: MediaRequestType.Delete,
      fileName: media.fileName,
      originalFileName: media.originalFileName || media.fileName,
      fileType: media.fileType,
      mimeType: media.mimeType || ext,
      fileSize: media.fileSize,
      s3Url: media.s3Url,
      entityId,
      tags: media.tags || [],
      metadata: media.metadata,
      isProfilePic: media.isProfilePic || false,
      mediaType: media.mediaType || 1,
    };
  });
};

export const applyDelete = (medias: any[], idsToRemove: string[]) =>
  medias.filter((m) => !idsToRemove.includes(m.id));

/* ---------------- Cover photo ----------------
 * A record's cover photo is one of its own media, marked with this tag. Tags
 * already travel through the media update call and come back on every read,
 * so a cover needs no field of its own on members or organisations. */
export const COVER_TAG = "cover";

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- media rows are loosely typed across modules
type AnyMedia = any;

export const isCoverMedia = (media: AnyMedia) =>
  Array.isArray(media?.tags) && media.tags.includes(COVER_TAG);

/** The link of the record's cover photo, if it has one. */
export const coverUrlOf = (medias?: AnyMedia[] | null): string | undefined => {
  const cover = (medias ?? []).find(isCoverMedia);
  return cover?.url || undefined;
};

const withCoverTag = (media: AnyMedia, on: boolean) => {
  const rest = (media.tags || []).filter((tag: string) => tag !== COVER_TAG);
  return on ? [...rest, COVER_TAG] : rest;
};

/** Update payloads that move the cover tag to `coverId` (null clears it) —
 *  only for the media whose tag actually changes. */
export const buildCoverPayload = (
  medias: AnyMedia[],
  entityId: string,
  coverId: string | null,
) =>
  medias
    .filter((m) => isCoverMedia(m) !== (m.id === coverId))
    .map((m) => ({
      ...buildRenamePayload(m, entityId, m.fileName)[0],
      metadata: m.metadata,
      tags: withCoverTag(m, m.id === coverId),
    }));

export const applyCover = (medias: AnyMedia[], coverId: string | null) =>
  medias.map((m) => ({ ...m, tags: withCoverTag(m, m.id === coverId) }));
